import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/cn';

import { Spinner } from './States';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  leading?: ReactNode;
  children: ReactNode;
}

const variantClass: Record<ButtonVariant, string> = {
  primary: 'bg-cta text-cta-fg active:brightness-95',
  secondary: 'bg-surface-2 text-fg active:brightness-110',
  ghost: 'bg-transparent text-accent active:bg-surface',
  danger: 'bg-danger/15 text-danger active:bg-danger/25',
};

/*
 * Заблокированная кнопка не должна выглядеть нажимаемой. Общий `opacity-50` оставлял у primary
 * приглушённый оранжевый — форма звала нажать, а подпись на нём переставала читаться. Поэтому
 * недоступный primary становится поверхностью, а не выцветшим акцентом.
 */
const disabledClass: Record<ButtonVariant, string> = {
  primary: 'disabled:bg-surface-2 disabled:text-muted',
  secondary: 'disabled:opacity-50',
  ghost: 'disabled:opacity-50',
  danger: 'disabled:opacity-50',
};

/*
 * Высота — минимальная, а не фиксированная (раунд 4, N9): на 320 px «Поделиться номером из
 * Telegram» переносилась на вторую строку и вылезала из кнопки высотой 44 px.
 */
const sizeClass: Record<ButtonSize, string> = {
  md: 'min-h-11 px-4 py-2 text-base leading-tight text-center',
  lg: 'min-h-13 px-5 py-2 text-lg leading-tight text-center',
};

/** Кнопка: primary — одно главное действие на экране, secondary — второстепенное, ghost — текстовое. */
export function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  leading,
  className,
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <button
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'rounded-control inline-flex items-center justify-center gap-2 font-semibold',
        'pressable press-scale',
        'select-none disabled:cursor-not-allowed',
        variantClass[variant],
        disabledClass[variant],
        sizeClass[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner size="sm" /> : leading}
      <span>{children}</span>
    </button>
  );
}
